import React, { useEffect, useState } from 'react';
import '../App.css';
import bookCover from '../assets/book-cover.png';
import { Card, Alert, Form, Input, Button, Row, Col, message } from "antd";
import {
  ReadOutlined,
  MailOutlined,
  QuestionCircleOutlined, 
  TrophyOutlined,
  UserOutlined,
  PhoneOutlined,
  TagOutlined,
} from '@ant-design/icons';
import { useNavigate } from "react-router-dom";

const { TextArea } = Input;

export default function HomePage() {

  const navigate = useNavigate();
  const [alertInfo, setAlertInfo] = useState(null);
  const [contactForm] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const CONTACT_API_URL = `${process.env.REACT_APP_API_URL}/contact-us`;

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/version`)
      .then((res) => res.json())
      .then((result) => setAlertInfo(result?.data?.card_data))
      .catch((error) => console.error("Failed to fetch version info:", error));
  }, []);

  const handleContactFinish = async (values) => {
    setSubmitting(true);
    try {
      const payload = {
        name: values.name,
        email: values.email,
        phone_number: values.phone || "",
        subject: values.subject,
        message: values.message,
      };

      const res = await fetch(CONTACT_API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });

      let data = null;
      try {
        data = await res.json();
      } catch {
        // no JSON body — fall back to generic message below
      }

      if (!res.ok) {
        const errMsg =
          data?.message ||
          (data?.errors && Object.values(data.errors).flat()[0]) ||
          "Something went wrong sending your message. Please try again.";
        throw new Error(errMsg);
      }

      message.success("Thanks for reaching out — we'll get back to you soon.");
      contactForm.resetFields();
    } catch (err) {
      message.error(err.message || "Something went wrong sending your message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main>
      <section className="bg-gradient-to-b from-[#F3F3F3] to-[#DBD7D7]/80 text-gray-700">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left column: book image and small badge */}
          <div className="flex items-center justify-center lg:justify-start">
            <div className="relative">
              <img src={bookCover} alt="Book Cover" className="w-56 lg:w-72 max-w-full shadow-2xl rounded-md transform -rotate-3" />
            </div>
          </div>

          {/* Right column: title, actions, bullets */}
          <div>
            {alertInfo?.message && alertInfo?.type != null && (
              <Alert
                description={<div dangerouslySetInnerHTML={{ __html: alertInfo.message }} />}
                type={alertInfo.type === 1 ? "error" : "warning"}
                className="mb-4"
              />
            )}
            <h1 className="text-3xl lg:text-4xl font-extrabold leading-tight">An Advanced Review of Speech–Language Pathology</h1>
            <p className="mt-4 text-lg text-gray-700">
              6th Edition: Practice Examinations — authors:{" "}
             <a
                href="authors#celeste-roseberry-mckibbin"
                className="hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label="Navigate to Celeste Roseberry-McKibbin author section"
              >
                Celeste Roseberry-McKibbin
              </a>
              ,{" "}
              <a
                href="authors#mn-hegde"
                className="hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label="Navigate to M. N. Hegde author section"
              >
                M. N. Hegde
              </a>
              ,{" "}
              <a
                href="authors#glen-tellis"
                className="hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label="Navigate to Glen M. Tellis author section"
              >
                Glen M. Tellis
              </a>
            </p>
            <div className="mt-6 flex flex-wrap gap-4">
              <a href="https://slp.advancedreviewpractice.com" className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-[#00AAB5] text-white font-semibold" target="_blank" rel="noopener noreferrer">Take a test</a>
              <a href="https://www.proedinc.com/products-40018.html" className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-[#a70050] text-white font-semibold" target="_blank" rel="noopener noreferrer">Buy the book</a>
            </div>

          </div>
        </div>
      </section>
      {!(alertInfo?.message && alertInfo.type === 1) && (
        <section className='bg-gradient-to-b from-[#DBD7D7]/80 to-[#F3F3F3] px-4 pb-8'>
          <div className="max-w-6xl mx-auto px-6 sm:px-8 pb-8 sm:pb-16 grid grid-cols-1 sm:grid-cols-2 gap-12 items-center">
            {/* --- CARD: Question of the Day --- */}
            <Card
              hoverable
              onClick={() => navigate("/question-of-the-day")}
              className="rounded-2xl shadow-sm border border-gray-200/50 transition-all duration-300 
               hover:shadow-xl hover:border-indigo-400/60 hover:-translate-y-1"
            >
              <div className="flex flex-col items-center text-center gap-4 py-4">

                {/* Icon Glow Animation */}
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-300/40 to-indigo-500/40 
                      flex items-center justify-center text-3xl relative
                      shadow-lg transition-all duration-300
                      animate-pulse"
                >
                  <QuestionCircleOutlined className="text-indigo-700 drop-shadow-[0_0_6px_rgba(99,102,241,0.6)]" />
                </div>

                <h1 className="font-bold text-lg tracking-wide">
                  Question of the Day
                </h1>

              </div>
            </Card>

            {/* --- CARD: Top 10 Scores --- */}
            <Card
              hoverable
              onClick={() => navigate("/top-scores")}
              className="rounded-2xl shadow-sm border border-gray-200/50 transition-all duration-300 
               hover:shadow-xl hover:border-yellow-400/60 hover:-translate-y-1"
            >
              <div className="flex flex-col items-center text-center gap-4 py-4 relative">

                {/* Trophy Icon with Glow */}
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-yellow-300/40 to-yellow-500/40
                      flex items-center justify-center text-3xl relative
                      shadow-lg transition-all duration-300
                      animate-pulse"
                >
                  <TrophyOutlined className="text-yellow-600 drop-shadow-[0_0_6px_rgba(234,179,8,0.6)]" />
                </div>

                <h1 className="font-bold text-lg tracking-wide">
                  Top 10 Test Scores
                </h1>

              </div>
            </Card>
          </div>
        </section>
      )}


      {/* --- About / Praxis SLP section (magenta background) --- */}
      <section id="about" className="py-14 bg-[#a70050] text-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
          <div className="lg:col-span-2">
            <h2 className="text-2xl font-bold">About the Praxis SLP — ETS</h2>

            <div className="mt-6 space-y-5 text-slate-100/95 leading-relaxed text-sm">
              <p>
                To determine which test you need to take and see the minimum qualifying score, find your licensure area below.
                To learn about a certain test, click on the test title and then find out
                <a className="underline font-semibold ml-1" href="http://www.ets.org/praxis/register" target="_blank" rel="noopener noreferrer">how to register for Praxis® tests</a>.
                Make sure to read the Praxis test retake policy.
              </p>

              <p>
                Unless it is mentioned that a calculator is allowed or required for a test, calculators may not be used on any
                Praxis tests (see
                <a className="underline font-semibold ml-1" href="http://www.ets.org/praxis/test_day/policies/calculators" target="_blank" rel="noopener noreferrer">Calculator Use</a>).
              </p>

              <p>
                <strong>Speech—Language Pathology Certification</strong><br />
                Note: Praxis Speech-Language Pathology (SLP) test scores (as of September 2014) are reported on a 100—200 score
                scale in one-point increments. This Praxis SLP test uses code <strong>5331</strong>. The required score for the
                American Speech-Language-Hearing Association (ASHA) and the state boards of examiners is <strong>162</strong>.
              </p>

              <p>
                <strong>ASHA</strong> will continue to accept a passing score on the Praxis test (both the 0330/5330 and the 5331 test
                codes) as long as it was obtained within five years of the date the application for certification is received by
                ASHA.
              </p>

              <p>
                For more details, visit
                <a className="underline font-semibold ml-1" href="https://praxis.ets.org/" target="_blank" rel="noopener noreferrer">
                https://praxis.ets.org/
                </a>.
              </p>
            </div>
          </div>
          <div className='flex flex-col gap-3'>
            <aside
              onClick={() =>
                window.open(
                  "https://www.proedinc.com/products-40018e.html",
                  "_blank"
                )
              }
              className="bg-white/10 rounded-lg p-6 h-44 flex flex-col justify-center text-center gap-5 cursor-pointer hover:bg-white/20"
            >
              <div className="w-12 h-12 rounded-md bg-white/20 flex items-center justify-center text-white text-3xl mx-auto">
                <ReadOutlined />
              </div>
              <h1 className="font-bold">Purchase E-Book</h1>
            </aside>
            <aside 
              onClick={() => window.location.href = "mailto:advancedreviewpractice@proedinc.com"}
              className="bg-white/10 rounded-lg p-6 h-44 flex flex-col justify-center text-center gap-5 cursor-pointer hover:bg-white/20"
            >
              <div className="w-12 h-12 rounded-md bg-white/20 flex items-center justify-center text-white text-3xl mx-auto"><MailOutlined /></div>
              <h1 className='font-bold'>Email Us</h1>
            </aside>
          </div>
        </div>
      </section>

      {/* --- Contact Us section --- */}
      <section id="contact" className="py-16 bg-[#F3F3F3]">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200/60 p-8 lg:p-10">
            <h2 className="text-2xl font-bold text-center text-gray-800">Contact Us</h2>
            <p className="mt-2 text-center text-gray-500">
              For any questions about the product, please complete the following:
            </p>

            <Form
              form={contactForm}
              layout="vertical"
              className="mt-3"
              onFinish={handleContactFinish}
              scrollToFirstError
            >
              <Form.Item
                label="Name"
                name="name"
                rules={[{ required: true, message: "Please enter your name" }]}
              >
                <Input
                  size="large"
                  prefix={<UserOutlined className="text-gray-400" />}
                  placeholder="Enter your name"
                />
              </Form.Item>

              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Email"
                    name="email"
                    rules={[
                      { required: true, message: "Please enter your email" },
                      { type: "email", message: "Enter a valid email address" },
                    ]}
                  >
                    <Input
                      size="large"
                      prefix={<MailOutlined className="text-gray-400" />}
                      placeholder="Enter your email"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Phone Number"
                    name="phone"
                    rules={[
                      {
                        validator: (_, value) =>
                          !value || /^[\d\s()+-]{7,}$/.test(value)
                            ? Promise.resolve()
                            : Promise.reject("Enter a valid phone number"),
                      },
                    ]}
                  >
                    <Input
                      size="large"
                      prefix={<PhoneOutlined className="text-gray-400" />}
                      placeholder="Enter your phone number"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                label="Subject"
                name="subject"
                rules={[{ required: true, message: "Please enter a subject" }]}
              >
                <Input
                  size="large"
                  prefix={<TagOutlined className="text-gray-400" />}
                  placeholder="What's this about?"
                />
              </Form.Item>

              <Form.Item
                label="Message"
                name="message"
                rules={[{ required: true, message: "Please enter a message" }]}
              >
                <TextArea rows={5} placeholder="Enter your message" showCount maxLength={1000} />
              </Form.Item>

              <Form.Item className="mb-0 flex justify-end">
                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  loading={submitting}
                  className="px-10 bg-[#a70050] hover:bg-[#8a0041] border-none"
                >
                  Send
                </Button>
              </Form.Item>
            </Form>
          </div>
        </div>
      </section>
    </main>
  );
}
